import * as api from '@gitbook/api-client';

import { AIFeature } from './ai';
import { checkIsCommunityProductKey, isLegacyProduct, PRICING_PRODUCTS } from './table_products';

/** A site or organization pricing feature. */
export type PricingFeature = api.SiteFeatureKey | api.OrganizationFeature;

/**
 * Availability of an organization feature on a billing product: a member limit for
 * `max-members`, an access flag for every other feature.
 */
type PricingFeatureValue<FeatureKey extends api.OrganizationFeature> =
    (typeof PRICING_FEATURES_MAP)[FeatureKey][api.BillingProduct];

type PricingFeatureDefinitionCommonProps = {
    // Organization features this one requires, transitively: access checks and upgrade prompts
    // cover them too, e.g. the site style guide needs AI Lens, which needs AI.
    dependsOn?: readonly Exclude<api.OrganizationFeature, api.OrganizationFeature.MaxMembers>[];

    // AI setting of the org that turns this feature off when disabled, regardless of the plan,
    // e.g. turning off AI translation disables translations even on Pro.
    aiFeature?: AIFeature;

    // Whether this feature will be summarized in the end-of-trial dialog
    showInTrialEndSummary?: boolean;

    // Generic name of the feature, e.g. `Unlimited members`
    title: string;

    // Tooltip label
    tooltipLabel: string;
};

/**
 * Complete definition for a feature.
 */
export type PricingFeatureDefinition<
    FeatureKey extends api.OrganizationFeature = api.OrganizationFeature,
> = PricingFeatureDefinitionCommonProps & {
    key: FeatureKey;
} & Record<api.BillingProduct, PricingFeatureValue<FeatureKey>>;

/**
 * Get the maximum number of allowed members for an organization
 */
export function getMaxMembersLimit(plan: api.Organization['plan']): number {
    return PRICING_FEATURES_MAP[api.OrganizationFeature.MaxMembers][plan];
}

/**
 * Determiners whether the given organization has access to a feature
 */
export function hasAccessToFeature(
    plan: api.Organization['plan'],
    featureKey: Exclude<api.OrganizationFeature, api.OrganizationFeature.MaxMembers>
): boolean {
    return PRICING_FEATURES_MAP[featureKey][plan];
}

/**
 * Organization pricing features, by key.
 */
const PRICING_FEATURES_MAP = {
    [api.OrganizationFeature.MaxMembers]: {
        key: api.OrganizationFeature.MaxMembers,
        title: 'Members',
        tooltipLabel: 'The maximum number of members in your organization.',

        free_2024: 1,
        plus_2024: Number.POSITIVE_INFINITY,
        pro_2024: Number.POSITIVE_INFINITY,
        enterprise_2024: Number.POSITIVE_INFINITY,
        community_2024: Number.POSITIVE_INFINITY,
        internal: Number.POSITIVE_INFINITY,
    },

    [api.OrganizationFeature.Ai]: {
        key: api.OrganizationFeature.Ai,
        title: 'AI',
        tooltipLabel: 'Enable AI features available on your organization and site plans.',
        free_2024: false,
        plus_2024: true,
        pro_2024: true,
        enterprise_2024: true,
        community_2024: true,
        internal: true,
    },

    [api.OrganizationFeature.AiLens]: {
        key: api.OrganizationFeature.AiLens,
        aiFeature: AIFeature.Agent,
        dependsOn: [api.OrganizationFeature.Ai],
        title: 'GitBook Agent',
        tooltipLabel:
            'Ask GitBook AI a question about your internal docs and get a summarized answer in seconds.',

        free_2024: false,
        plus_2024: false,
        pro_2024: true,
        enterprise_2024: true,
        community_2024: true,
        internal: true,
    },

    [api.OrganizationFeature.PdfExport]: {
        key: api.OrganizationFeature.PdfExport,
        showInTrialEndSummary: true,
        title: 'PDF Export',
        tooltipLabel: 'Easily export pages of your internal docs in PDF format.',

        free_2024: false,
        plus_2024: false,
        pro_2024: true,
        enterprise_2024: true,
        community_2024: true,
        internal: true,
    },

    [api.OrganizationFeature.AiWriting]: {
        key: api.OrganizationFeature.AiWriting,
        aiFeature: AIFeature.Agent,
        dependsOn: [api.OrganizationFeature.Ai],
        title: 'AI writing and editing',
        tooltipLabel:
            'Use GitBook AI to write, edit, summarize, or translate content for you. It can even proof-read your documentation.',

        free_2024: false,
        plus_2024: false,
        pro_2024: true,
        enterprise_2024: true,
        community_2024: true,
        internal: true,
    },

    [api.OrganizationFeature.ReusableContent]: {
        key: api.OrganizationFeature.ReusableContent,
        title: 'Reusable content',
        tooltipLabel:
            'Create reusable blocks of content that automatically sync across your entire documentation when they’re updated.',

        free_2024: false,
        plus_2024: false,
        pro_2024: true,
        enterprise_2024: true,
        community_2024: true,
        internal: true,
    },

    [api.OrganizationFeature.ChangeRequestsMergeRules]: {
        key: api.OrganizationFeature.ChangeRequestsMergeRules,
        showInTrialEndSummary: true,
        title: 'Merge rules',
        tooltipLabel: 'Configure merge rules to determine if change requests can be merged.',

        free_2024: false,
        plus_2024: false,
        pro_2024: true,
        enterprise_2024: true,
        community_2024: true,
        internal: true,
    },

    [api.OrganizationFeature.MembersAdvancedRoles]: {
        key: api.OrganizationFeature.MembersAdvancedRoles,
        showInTrialEndSummary: true,
        title: 'Advanced roles',
        tooltipLabel:
            'In addition to the basic roles, you can assign members the reviewer and commenter roles.',

        free_2024: false,
        plus_2024: false,
        pro_2024: true,
        enterprise_2024: true,
        community_2024: true,
        internal: true,
    },

    [api.OrganizationFeature.MembersTeams]: {
        key: api.OrganizationFeature.MembersTeams,
        showInTrialEndSummary: true,
        title: 'Teams',
        tooltipLabel:
            'Group users into teams to make it easier to manage content access and permissions.',

        free_2024: false,
        plus_2024: false,
        pro_2024: true,
        enterprise_2024: true,
        community_2024: true,
        internal: true,
    },

    [api.OrganizationFeature.MembersTeamOwners]: {
        key: api.OrganizationFeature.MembersTeamOwners,
        title: 'Team owners',
        tooltipLabel: 'Delegate the management of a team to a specific member.',

        free_2024: false,
        plus_2024: false,
        pro_2024: false,
        enterprise_2024: true,
        community_2024: true,
        internal: true,
    },

    [api.OrganizationFeature.Translations]: {
        key: api.OrganizationFeature.Translations,
        aiFeature: AIFeature.Translation,
        dependsOn: [api.OrganizationFeature.Ai],
        title: 'AI Translations',
        tooltipLabel: 'Translate your content into multiple languages with AI.',

        free_2024: false,
        plus_2024: false,
        pro_2024: true,
        enterprise_2024: true,
        community_2024: true,
        internal: true,
    },

    [api.OrganizationFeature.SecuritySaml]: {
        key: api.OrganizationFeature.SecuritySaml,
        title: 'SAML SSO',
        tooltipLabel:
            'Allow organization members to sign into GitBook using a third-party identity provider.',

        free_2024: false,
        plus_2024: false,
        pro_2024: false,
        enterprise_2024: true,
        community_2024: false,
        internal: true,
    },

    [api.OrganizationFeature.Scim]: {
        key: api.OrganizationFeature.Scim,
        title: 'SCIM provisioning',
        tooltipLabel: 'Provision organization members automatically from an identity provider.',

        free_2024: false,
        plus_2024: false,
        pro_2024: false,
        enterprise_2024: true,
        community_2024: false,
        internal: true,
    },
} as const satisfies {
    [Feature in api.OrganizationFeature]: PricingFeatureDefinitionCommonProps & {
        key: Feature;
    } & Record<
            api.BillingProduct,
            Feature extends api.OrganizationFeature.MaxMembers ? number : boolean
        >;
};

export const PRICING_FEATURES: readonly PricingFeatureDefinition[] =
    Object.values(PRICING_FEATURES_MAP);

/**
 * Get the pricing definition of an organization feature.
 */
export function getFeatureDefinition(
    featureKey: api.OrganizationFeature | undefined
): PricingFeatureDefinition | undefined {
    return featureKey ? PRICING_FEATURES_MAP[featureKey] : undefined;
}

/**
 * Read the availability value of a feature for a given billing product.
 */
function getFeatureAvailability<FeatureKey extends api.OrganizationFeature>(
    feature: PricingFeatureDefinition<FeatureKey>,
    product: api.BillingProduct
): PricingFeatureValue<FeatureKey> {
    return feature[product];
}

/**
 * Find the first non-legacy, non-community plan that includes a given feature.
 */
export function findRequiredPlanForFeature(featureKey: api.OrganizationFeature) {
    const feature = PRICING_FEATURES_MAP[featureKey];

    return PRICING_PRODUCTS.find(
        (product) =>
            getFeatureAvailability(feature, product.key) &&
            !checkIsCommunityProductKey(product.key) &&
            !isLegacyProduct(product.key)
    );
}

/**
 * Finds the first feature that requires an upgrade for the given organization plan.
 * Returns undefined if no upgrade is required.
 */
export function getBlockedFeature(
    featureKeys: api.OrganizationFeature[],
    organizationPlan: api.BillingProduct
): api.OrganizationFeature | undefined {
    return featureKeys.find(
        (featureKey) => !getFeatureAvailability(PRICING_FEATURES_MAP[featureKey], organizationPlan)
    );
}
